import { CommonModule } from "@angular/common";
import { Component, DestroyRef, inject, Input } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { FormControl, ReactiveFormsModule } from "@angular/forms";
import { MatButtonModule } from "@angular/material/button";
import { MatDialog } from "@angular/material/dialog";
import {
  Collaborator,
  CollaboratorInvitation,
  CollaboratorMutationResponse,
} from "@models/collaborator";
import { IconFieldModule } from "primeng/iconfield";
import { InputIconModule } from "primeng/inputicon";
import { InputTextModule } from "primeng/inputtext";
import { PaginatorState } from "primeng/paginator";
import { TabsModule } from "primeng/tabs";
import {
  combineLatest,
  catchError,
  EMPTY,
  finalize,
  startWith,
  Subject,
  switchMap,
  take,
  takeUntil,
  tap,
  exhaustMap,
  filter,
} from "rxjs";
import { PdvPermissionDialogComponent } from "./pdv-permission-dialog.component";
import { PdvPinDialogComponent } from "./pdv-pin-dialog.component";
import { SnackBarService } from "@services/snack-bar.service";
import { ButtonComponent } from "../../../../shared/components/button/button.component";
import { LoadingComponent } from "../../../../shared/components/loading/loading.component";
import { getParams } from "../../../../utils/utils";
import { CollaboratorsService } from "../../../services/collaborators.service";
import { EventsManagementService } from "../../../services/events-management.service";
import { ConfirmActionModalComponent } from "../../../shared/modals/confirm-action-modal/confirm-action-modal.component";
import {
  AddEmployeeDialogComponent,
  AddEmployeeDialogData,
} from "./add-employee-dialog/add-employee-dialog.component";
import { EmployeesInvitationsTableComponent } from "./employees-invitations-table/employees-invitations-table.component";
import { EmployeesTableComponent } from "./employees-table/employees-table.component";

@Component({
  selector: "app-employees-tab",
  imports: [
    CommonModule,
    EmployeesTableComponent,
    ReactiveFormsModule,
    IconFieldModule,
    InputTextModule,
    InputIconModule,
    MatButtonModule,
    ButtonComponent,
    LoadingComponent,
    TabsModule,
    EmployeesInvitationsTableComponent,
  ],
  templateUrl: "./employees-tab.component.html",
  styleUrl: "./employees-tab.component.scss",
})
export class EmployeesTabComponent {
  @Input() eventId: number | null = null;
  @Input() operatorsOnly = false;
  @Input() readOnly = false;
  @Input() canGeneratePin = false;
  tableFilter = new FormControl("", { nonNullable: true });

  private _dialog = inject(MatDialog);
  private _eventsService = inject(EventsManagementService);
  private _collaboratorsService = inject(CollaboratorsService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly snackbar = inject(SnackBarService);
  private pinPromptPending = false;

  loading = false;

  private readonly refreshInvitations$ = new Subject<void>();
  private readonly page$ = new Subject<PaginatorState>();

  eventEmployee$ = combineLatest([
    getParams("id"),
    this.page$.pipe(startWith(null)),
    this.tableFilter.valueChanges.pipe(startWith("")),
    this.refreshInvitations$.pipe(startWith(null)),
  ]).pipe(
    switchMap(([eventId, page, searchTerm]) =>
      this._eventsService.getCollaborators(eventId, page, searchTerm),
    ),
  );

  eventEmployeeInvitation$ = combineLatest([
    getParams("id"),
    this.page$.pipe(startWith(null)),
    this.tableFilter.valueChanges.pipe(startWith("")),
    this.refreshInvitations$.pipe(startWith(null)),
  ]).pipe(
    switchMap(([eventId, page, searchTerm]) =>
      this._eventsService.getCollaboratorsInvitations(
        eventId,
        page,
        searchTerm,
      ),
    ),
  );

  handleResendInvite(employee: CollaboratorInvitation) {
    const dialogRef = this._dialog.open(ConfirmActionModalComponent, {
      width: "500px",
      data: {
        modalTitle: "Reenviar convite?",
        modalContent: `Confirma que deseja reenviar o convite para esse colaborador? </br> O e-mail <b>${employee.email}</b> está correto?`,
        confirmMessage: "Confirmar",
        cancelMessage: "Cancelar",
      },
    });

    dialogRef.afterClosed().subscribe((res) => {
      if (res) {
        this.loading = true;

        this._collaboratorsService
          .resendInvitation(employee)
          .pipe(
            take(1),
            finalize(() => (this.loading = false)),
          )
          .subscribe(() => {
            this.refreshInvitations$.next();
            this.loading = false;
          });
      }
    });
  }

  handleRemoveEmployee(employee: Collaborator) {
    const dialogRef = this._dialog.open(ConfirmActionModalComponent, {
      width: "500px",
      data: {
        modalTitle: "Remover empregado?",
        modalContent:
          "Deseja realmente tirar as permissões de acesso desse colaborador?",
        confirmMessage: "Confirmar",
        cancelMessage: "Cancelar",
      },
    });

    dialogRef.afterClosed().subscribe((res) => {
      if (res) {
        this.loading = true;
        this._eventsService
          .removeCollaborator(this.eventId!, employee)
          .pipe(
            take(1),
            finalize(() => (this.loading = false)),
          )
          .subscribe(() => {
            this.refreshInvitations$.next();
            this.loading = false;
          });
      }
    });
  }

  handleRevokeInvitation(employee: CollaboratorInvitation) {
    const dialogRef = this._dialog.open(ConfirmActionModalComponent, {
      width: "500px",
      data: {
        modalTitle: "Remover empregado?",
        modalContent:
          "Deseja realmente cancelar o convite para esse colaborador?",
        confirmMessage: "Confirmar",
        cancelMessage: "Cancelar",
      },
    });

    dialogRef.afterClosed().subscribe((res) => {
      if (res) {
        this.loading = true;
        this._collaboratorsService
          .revokeInvitation(employee.id)
          .pipe(
            take(1),
            finalize(() => (this.loading = false)),
          )
          .subscribe(() => {
            this.refreshInvitations$.next();
          });
      }
    });
  }

  addEmployee() {
    const eventId = this.eventId;
    if (!eventId) return;
    const dialogRef = this._dialog.open(AddEmployeeDialogComponent, {
      width: "920px",
      maxWidth: "calc(100vw - 32px)",
      maxHeight: "calc(100dvh - 32px)",
      data: {
        eventId,
        operatorsOnly: this.operatorsOnly,
      } as AddEmployeeDialogData,
    });

    dialogRef.componentInstance.submitted
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        takeUntil(dialogRef.afterClosed()),
        exhaustMap((payload) => {
          dialogRef.componentInstance.loading = true;
          dialogRef.componentInstance.apiError = "";
          dialogRef.disableClose = true;
          return this._eventsService.addCollaborator(eventId, payload).pipe(
            tap((response) => {
              dialogRef.close(true);
              this.refreshInvitations$.next();
              this.showPin(response, true, payload.collaborator.email);
            }),
            catchError((error) => {
              dialogRef.componentInstance.apiError = this.errorMessage(error);
              return EMPTY;
            }),
            finalize(() => {
              dialogRef.componentInstance.loading = false;
              dialogRef.disableClose = false;
            }),
          );
        }),
      )
      .subscribe();
  }

  handlePageChange(page: PaginatorState) {
    this.page$.next(page);
  }

  editPdvPermission(
    person: Collaborator | CollaboratorInvitation,
    pending = false,
  ) {
    const eventId = this.eventId;
    if (!eventId) return;
    const dialog = this._dialog.open(PdvPermissionDialogComponent, {
      width: "920px",
      maxWidth: "calc(100vw - 32px)",
      maxHeight: "calc(100dvh - 32px)",
      data: { person, pending },
    });
    dialog.componentInstance.submitted
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        takeUntil(dialog.afterClosed()),
        exhaustMap((settings) => {
          dialog.componentInstance.loading = true;
          dialog.componentInstance.apiError = "";
          dialog.disableClose = true;
          return this._eventsService
            .updatePdvSettings(eventId, person, settings, pending)
            .pipe(
              tap((response) => {
                dialog.close();
                this.refreshInvitations$.next();
                this.showPin(response, pending, person.email);
              }),
              catchError((error) => {
                dialog.componentInstance.apiError = this.errorMessage(error);
                return EMPTY;
              }),
              finalize(() => {
                dialog.componentInstance.loading = false;
                dialog.disableClose = false;
              }),
            );
        }),
      )
      .subscribe();
  }

  generateMyPin() {
    const eventId = this.eventId;
    if (
      this.canGeneratePin !== true ||
      !eventId ||
      this.loading ||
      this.pinPromptPending
    )
      return;
    this.pinPromptPending = true;
    this._dialog
      .open(ConfirmActionModalComponent, {
        width: "500px",
        data: {
          modalTitle: "Gerar novo PIN de autorização?",
          modalContent:
            "O PIN anterior deste evento deixará de funcionar. O novo PIN será exibido uma única vez. Somente produtor, gerente ou coordenador autorizado pode gerar PIN.",
          confirmMessage: "Gerar novo PIN",
          cancelMessage: "Cancelar",
        },
      })
      .afterClosed()
      .pipe(
        take(1),
        filter(Boolean),
        takeUntilDestroyed(this.destroyRef),
        exhaustMap(() => {
          this.loading = true;
          return this._eventsService.generateMyPdvPin(eventId).pipe(
            tap((response) =>
              this.showPin(response, false, "Meu PIN neste evento"),
            ),
            catchError((error) => {
              this.snackbar.openSnackBar(
                this.errorMessage(error),
                "Fechar",
                "error",
              );
              return EMPTY;
            }),
            finalize(() => {
              this.loading = false;
              this.refreshInvitations$.next();
            }),
          );
        }),
        finalize(() => {
          this.pinPromptPending = false;
        }),
      )
      .subscribe();
  }

  private showPin(
    response: CollaboratorMutationResponse,
    pending: boolean,
    email?: string,
  ) {
    if (typeof response.pin !== "string" || !/^\d{6}$/.test(response.pin))
      return;
    this._dialog.open(PdvPinDialogComponent, {
      width: "500px",
      disableClose: true,
      data: { pin: response.pin, pending, email },
    });
    response.pin = undefined;
  }

  private errorMessage(error: unknown) {
    const message = (error as { error?: { error?: unknown } })?.error?.error;
    return typeof message === "string"
      ? message
      : "Não foi possível salvar. Confira os dados e tente novamente.";
  }

  downloadReport() {
    if (!this.eventId) return;
    this.loading = true;
    this._eventsService
      .downloadCollaboratorsReport(this.eventId)
      .pipe(finalize(() => (this.loading = false)))
      .subscribe({
        next: (blob) => {
          const url = URL.createObjectURL(blob);
          const a = document.createElement("a");
          a.href = url;
          a.download = "relatorio_colaboradores.pdf";
          a.click();
          URL.revokeObjectURL(url);
        },
      });
  }
}
